<template>
    <div class="d-flex align-items-center overflow-auto" :class="{ 'mr-4': !isTablet }">
        <div
            class="ml-16 mr-4 d-flex justify-content-center align-items-center"
            style="height: 32px; width: 32px; min-height: 32px; min-width: 32px">
            <Icon
                name="far fa-search"
                size="1-25x"
                @click="handleSearch"/>
        </div>
        <div
            v-if="isTablet"
            class="d-flex align-items-center justify-content-center"
            style="height: 40px; width: 40px; min-height: 40px; min-width: 40px"
            @click="scrollLeft">
            <Icon
                v-if="categoryOverflow"
                ariaLabel="Scroll Left"
                :name="left ? 'far fa-chevron-left' : 'fal fa-chevron-left'"
                :class="{ 'text-primary-700': left }"
                size="1x"/>
        </div>
        <div ref="carousel" v-resize="checkOverflow" class="categories" :style=" categoryOverflow ? {} : { justifyContent: 'center' }" @scroll.passive="handleScroll">
            <div
                v-for="(category, key) in categories"
                :id="`category-id=${category.id}`"
                :key="key"
                class="px-12"
                style="scroll-snap-align: start; border-left: 0; border-right: 0;"
                v-resize="computeOffset">
                <p
                    class="category-button py-12 tr16"
                    style=" border-left: 0; border-right: 0"
                    :class="{ 'category-highlight tm16': activeCategory === category.id }"
                    @click="handleClick(category.id)">
                    {{ category.name }}
                </p>
            </div>
        </div>
        <div
            v-if="isTablet"
            class="d-flex align-items-center justify-content-center"
            style="height: 40px; width: 40px; min-height: 40px; min-width: 40px"
            @click="scrollRight">
            <Icon
                v-if="categoryOverflow"
                ariaLabel="Scroll Right"
                :name="right ? 'far fa-chevron-right' : 'fal fa-chevron-right'"
                :class="{ 'text-primary-700': right }"
                size="1x"/>
        </div>
    </div>
</template>
<script setup lang="ts">
import {
    ref,
    watch,
    nextTick,
    onMounted,
} from 'vue';
import {Icon} from '@helcim/blizzard';
import {useScreenDimensions} from '../../compositions/useScreenDimensions';
import {Category} from '../../store/products';

const props = defineProps<{
    categories: Category[],
    activeCategory: number,
}>();

const emit = defineEmits<{
    (event: 'toggle-search'): void,
    (event: 'select-category', id: number): void,
}>();

const carousel = ref<HTMLElement>(null);
const { isTablet } = useScreenDimensions();

const categoryOverflow = ref(true);

const offsets = ref(new Map<number, number>([]));
const neighbours = ref(new Map<number, [number, number]>(props.categories.map((v, index, array) => {
    return [v.id, [array[index - 1]?.id || null, array[index + 1]?.id || null]];
})));

const currentCategory = ref(props.categories[0].id);
const destination = ref<number>(null);

const right = ref(true);
const left = ref(false);

function handleSearch() {
    emit('toggle-search');
}

function handleClick(id: number) {
    emit('select-category', id);
    destination.value = id;
}

function handleScroll() {
    left.value = carousel.value.scrollLeft > 0;
    right.value = carousel.value.scrollLeft < carousel.value.scrollWidth - carousel.value.offsetWidth;
    for(const [k, v] of offsets.value.entries()) {
        if(Math.floor(v) >= Math.ceil(carousel.value.scrollLeft) + 5) {
            break;
        }
        if(currentCategory.value !== k) {
            currentCategory.value = k;
        }
    }
}

function scrollLeft() {
    if(!left.value) {
        return;
    }
    scrollCarousel(offsets.value.get(neighbours.value.get(currentCategory.value)[0]));
}

function scrollRight() {
    if(!right.value) {
        return;
    }
    scrollCarousel(offsets.value.get(neighbours.value.get(currentCategory.value)[1]));
}

function computeOffset(entries: ResizeObserverEntry[]) {
    entries.forEach(entry => {
        const element = entry.target;
        if (element instanceof HTMLDivElement && element.id) {
            offsets.value.set(+element.id.split('=').slice(-1), element.offsetLeft);
        }
    });
}

function scrollCarousel(value: number) {
    carousel.value.scrollTo({
        left: value,
        behavior: 'smooth',
    });
}

watch([destination, () => props.activeCategory, offsets], ([dest, id, offset]) => {
    if(!carousel.value) {
        return;
    }
    if(dest === id) {
        destination.value = null;
        return;
    }
    if(dest !== null) {
        scrollCarousel(offset.get(dest));
        return;
    }
    nextTick(() => scrollCarousel(offset.get(id)));
}, { deep: true });

const checkOverflow = () => {
    categoryOverflow.value = carousel.value.scrollWidth > carousel.value.getBoundingClientRect().width;
};

onMounted(checkOverflow);
</script>
